import { t } from '@lingui/macro'
import { Form, Formik } from 'formik'
import styled, { useTheme } from 'styled-components'
import * as Yup from 'yup'

import { FormikTextArea } from '@/@bothrs-ui/web/components/forms/text-area/FormikTextArea'
import { FlexColumn } from '@/@bothrs-ui/web/components/layout'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { P } from '@/@bothrs-ui/web/components/typography/Body'
import { H3 } from '@/@bothrs-ui/web/components/typography/Heading'
import { breakpoints } from '@/config/layout'
import { CareQuestionDetailContentLayout } from '@/modules/care-question/components/care-question-detail-content-layout/CareQuestionDetailContentLayout'

import type { AppHeaderProps } from '@/@bothrs-ui/web/components/navigation/header/AppHeader.types'

type FormikValues = {
  message?: string
}

type Props = {
  header?: AppHeaderProps
  progress?: { min: number; max: number; value: number }
  onNext: (message: string) => void
  title?: string
  description?: string
  placeholder?: string
  buttonLabel?: string
  maxLength?: number
}

export const QuestionStep = ({
  header,
  progress,
  onNext,
  title = t({
    id: 'care-question.question-flow.title',
    message: 'Ik heb een vraag',
  }),
  description = t({
    id: 'care-question.question-flow.input.description',
    message: 'Geef meer details over je situatie of vraag.',
  }),
  placeholder = t({
    id: 'care-question.question-flow.input.placeholder',
    message: 'Typ je vraag',
  }),
  buttonLabel = t({
    id: 'globals.continue',
    message: 'Doorgaan',
  }),
  maxLength = 500,
}: Props) => {
  const theme = useTheme()

  const handleNext = (values: FormikValues) => {
    if (!values.message) return
    onNext(values.message)
  }

  return (
    <Formik
      initialValues={{ message: undefined }}
      onSubmit={handleNext}
      validationSchema={Yup.object().shape({
        message: Yup.string().nullable().max(maxLength, `Maximum length is ${maxLength} characters.`),
      })}
      validateOnChange
      validateOnMount
    >
      <Form style={{ height: '100%' }}>
        <CareQuestionDetailContentLayout
          header={header}
          progress={progress}
          bottom={
            <Button variant='Primary' type='submit'>
              {buttonLabel}
            </Button>
          }
        >
          <StContentContainer gap={theme.UI.Spacing.Space8}>
            {/* TITLE */}
            <FlexColumn gap={theme.UI.Spacing.Space4}>
              <H3 noMargin>{title}</H3>
              <P type='BodyLargeRegular' style={{ fontWeight: 400 }}>
                {description}
              </P>
            </FlexColumn>

            {/* INPUT */}
            <FormikTextArea name='message' maxLength={maxLength} required={true} placeholder={placeholder} />
          </StContentContainer>
        </CareQuestionDetailContentLayout>
      </Form>
    </Formik>
  )
}

const StContentContainer = styled(FlexColumn)`
  justify-content: flex-end;
  flex-grow: 1;

  @media (${breakpoints.fromS}) {
    justify-content: flex-start;
  }
`
